import { BookkeepingProgressionInfo } from '@/features/jobs/bookkeeping/BookkeepingProgressionInfo'
import type { BookkeepingStore } from '@/features/jobs/bookkeeping/BookkeepingStore'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { SnackbarFactory } from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { Subject } from 'rxjs'

export class BookkeepingConfirmationAccessDialogViewModel
  implements ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Used to notify when the user wants to choose a different platform.
   */
  private readonly _onChooseBookkeepingPlatform = new Subject()

  /**
   * Used to notify when the user confirms access.
   */
  private readonly _onConfirmProvidedAccess = new Subject()

  /**
   * The modal.
   */
  readonly modal = new ModalViewModel()

  /**
   * The confirm dialog view model.
   */
  readonly confirmDialogVm = new ModalViewModel()

  /**
   * Emits a value if the user wants to choose a platform.
   */
  readonly chosenBookkeepingPlatform$ =
    this._onChooseBookkeepingPlatform.asObservable()

  /**
   * Emits a value whenever the user confirms access provided.
   */
  readonly confirmedProvidedAccess$ =
    this._onConfirmProvidedAccess.asObservable()

  @observable
  accessor bookkeepingProgressionInfo: BookkeepingProgressionInfo | null =
    BookkeepingProgressionInfo.none

  constructor(
    private readonly _job: SubmittedJob,
    private readonly _store: BookkeepingStore,
    private readonly _newSnackbar: SnackbarFactory,
  ) {}

  @computed
  get platformConnection() {
    return this.bookkeepingProgressionInfo?.platformConnection ?? null
  }
  /**
   * The bookkeeping platform to confirm access.
   */
  @computed
  get bookkeepingPlatform() {
    return this.platformConnection?.type ?? 'Other'
  }

  /**
   * The connection name.
   */
  @computed
  get connectionName() {
    if (this.platformConnection?.type === 'Xero') {
      return this.platformConnection.xero.tenantName
    }

    if (this.platformConnection?.type === 'QuickBooks') {
      return this.platformConnection.quickBooks.companyName
    }

    return 'Other'
  }

  /** @inheritdoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritdoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  show(bookkeepingProgressionInfo: BookkeepingProgressionInfo | null) {
    this.bookkeepingProgressionInfo = bookkeepingProgressionInfo
    this.modal.show()
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  close() {
    this.modal.close()
  }

  /**
   * Chooses bookkeeping platform.
   */
  @action.bound
  chooseBookkeepingPlatform() {
    this._onChooseBookkeepingPlatform.next(undefined)
  }

  /**
   * Copies a given value to the clipboard.
   */
  @action.bound
  async copyToClipboard(value: string) {
    const snackbar = this._newSnackbar()

    try {
      await navigator.clipboard.writeText(value)
      snackbar
        .type('success')
        .saying('Copied!', `${value} was copied to the clipboard.`)
        .show()
        .autoDismiss()
    } catch (err: unknown) {
      snackbar.fromError(err)
    }
  }

  /**
   * Confirms the access.
   */
  confirmAccess = task.resolved(async () => {
    const snackbar = this._newSnackbar()

    try {
      await this._store.markAccessAsGranted(this._job.id)

      snackbar
        .type('success')
        .saying('Confirmed!', 'You confirmed access was granted.')
        .show()
        .autoDismiss()

      this._onConfirmProvidedAccess.next(undefined)
    } catch (err: unknown) {
      snackbar.fromError(err)
    }
  })
}
